import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Chip, LinearProgress, Alert, Collapse, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Tooltip
} from '@mui/material';
import { Layers, Play, RefreshCw, ChevronDown, ChevronUp, Database, Trash2, Eye } from 'lucide-react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { useTeam } from '../context/TeamContext';
import { AdminPanelSkeleton } from './adminPanelContent';

const ANALYZER_DEFS = [
  { type: 'plot_actions', label: 'Сюжетные действия', desc: 'Ключевые повороты сюжета, выборы и последствия' },
  { type: 'images', label: 'Изображения', desc: 'Визуальный контент и его связь с главами' },
  { type: 'characters', label: 'Персонажи', desc: 'Психотипы, реакции, портреты персонажей' },
  { type: 'chapters', label: 'Главы', desc: 'Структура, пейсинг и эмоциональные арки' },
  { type: 'premium_choices', label: 'Премиум-выборы', desc: 'Анализ размещения и ценности' },
  { type: 'chapter_hooks', label: 'Хуки глав', desc: 'Оценка вовлечённости на концах глав' },
  { type: 'escalation', label: 'Эскалация', desc: 'Развитие отношений по персонажам' }
];

/* Field display config per analyzer type */
const FIELD_LABELS = {
  plot_actions: {
    _nested: 'plot_points',
    title: 'Название', summary: 'Описание', characters_involved: 'Персонажи',
    chapter_name: 'Глава', circumstances: 'Обстоятельства', choices_made: 'Выборы',
    consequences: 'Последствия', related_chapters: 'Связанные главы', emotional_tone: 'Тон'
  },
  characters: {
    psychotype: 'Психотип', key_traits: 'Черты', communication_style: 'Стиль общения',
    emotional_patterns: 'Эмоции', relationships: 'Отношения', character_arc: 'Арка',
    strengths: 'Сильные стороны', weaknesses: 'Слабые стороны', portrait_summary: 'Портрет'
  },
  images: {
    description: 'Описание', objects: 'Объекты', mood: 'Настроение',
    characters_visible: 'Персонажи', setting: 'Место', art_style: 'Стиль'
  },
  chapters: {
    summary: 'Краткое содержание', structure: 'Структура', pacing: 'Пейсинг',
    emotional_arc: 'Эмоц. арка', key_decisions: 'Решения', characters_present: 'Персонажи',
    themes: 'Темы', strengths: 'Сильные стороны', improvement_areas: 'Улучшения'
  },
  premium_choices: {
    _nested: 'premium_moments',
    chapter_name: 'Глава', context: 'Контекст', premium_options: 'Премиум опции',
    free_options: 'Бесплатные опции', value_proposition: 'Ценность',
    emotional_weight: 'Эмоц. вес', placement_quality: 'Качество размещения'
  },
  chapter_hooks: {
    hook_type: 'Тип хука', cliffhanger_strength: 'Сила клиффхэнгера',
    unresolved_threads: 'Незакрытые линии', emotional_charge: 'Эмоц. заряд',
    curiosity_triggers: 'Триггеры любопытства', player_motivation: 'Мотивация игрока',
    improvement_suggestions: 'Предложения', overall_score: 'Оценка'
  },
  escalation: {
    escalation_phases: 'Фазы', overall_arc: 'Общая арка',
    escalation_quality: 'Качество', pacing_assessment: 'Пейсинг', suggestions: 'Предложения'
  }
};

function FieldValue({ value }) {
  if (value == null) return null;
  if (Array.isArray(value)) {
    if (value.length === 0) return <Typography variant="body2" color="text.disabled">—</Typography>;
    // Array of objects (e.g. escalation_phases)
    if (typeof value[0] === 'object') {
      return (
        <Box display="flex" flexDirection="column" gap={0.75}>
          {value.map((obj, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1, borderColor: 'rgba(255,255,255,0.06)', bgcolor: 'rgba(255,255,255,0.02)' }}>
              {Object.entries(obj).map(([k, v]) => (
                <Box key={k} display="flex" gap={0.75} alignItems="baseline" mb={0.25}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', minWidth: 80 }}>{k}:</Typography>
                  <FieldValue value={v} />
                </Box>
              ))}
            </Paper>
          ))}
        </Box>
      );
    }
    return (
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {value.map((v, i) => (
          <Chip key={i} label={v} size="small" sx={{ fontSize: '0.75rem', height: 22 }} />
        ))}
      </Box>
    );
  }
  return <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.5 }}>{String(value)}</Typography>;
}

function StructuredCard({ data, analyzerType }) {
  const config = FIELD_LABELS[analyzerType] || {};
  if (!data || typeof data !== 'object') return null;

  // Some analyzers nest items in an array field
  const nestedKey = config._nested;
  if (nestedKey && Array.isArray(data[nestedKey])) {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        {data[nestedKey].map((sub, i) => (
          <Box key={i}>
            {Object.entries(config).filter(([k]) => k !== '_nested').map(([field, label]) => {
              const val = sub[field];
              if (val == null || (Array.isArray(val) && val.length === 0)) return null;
              return (
                <Box key={field} display="flex" gap={1} alignItems="baseline" mb={0.5}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', minWidth: 100, flexShrink: 0 }}>{label}</Typography>
                  <FieldValue value={val} />
                </Box>
              );
            })}
            {i < data[nestedKey].length - 1 && <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', my: 0.75 }} />}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {Object.entries(config).map(([field, label]) => {
        const val = data[field];
        if (val == null || (Array.isArray(val) && val.length === 0)) return null;
        return (
          <Box key={field} display="flex" gap={1} alignItems="baseline" mb={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', minWidth: 100, flexShrink: 0 }}>{label}</Typography>
            <FieldValue value={val} />
          </Box>
        );
      })}
    </Box>
  );
}

export default function EmbeddingsPanel({ getToken }) {
  const { activeTeam } = useTeam();
  const teamId = activeTeam?.id;
  const features = activeTeam?.features || [];

  const enabledTypes = ANALYZER_DEFS.filter((a) => features.includes(`emb-${a.type}`));

  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  // Items preview state
  const [itemsCache, setItemsCache] = useState({}); // { [type]: { items, total, loading } }

  // Per-item delete confirmation: id of item awaiting second click
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Clear confirmation modal
  const [clearModal, setClearModal] = useState(null); // analyzerType string or null

  const loadStatus = useCallback(async () => {
    if (!teamId) return;
    try {
      const h = await getAuthHeaders(getToken);
      const res = await fetch(`${API_BASE_URL}/api/analyzers/${teamId}/status`, { headers: h });
      if (!res.ok) throw new Error('Failed to load status');
      const data = await res.json();
      const map = {};
      (data.analyzers || []).forEach((a) => { map[a.type] = a; });
      setStatuses(map);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [teamId, getToken]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Poll while any analyzer is running
  useEffect(() => {
    const hasRunning = Object.values(statuses).some((s) => s.running);
    if (!hasRunning) return;
    const t = setInterval(loadStatus, 5000);
    return () => clearInterval(t);
  }, [statuses, loadStatus]);

  // Load items preview when a group is expanded
  const loadItems = useCallback(async (type) => {
    if (!teamId) return;
    setItemsCache((prev) => ({ ...prev, [type]: { ...(prev[type] || {}), loading: true } }));
    try {
      const h = await getAuthHeaders(getToken);
      const res = await fetch(`${API_BASE_URL}/api/analyzers/${teamId}/items/${type}?limit=100`, { headers: h });
      if (!res.ok) throw new Error('Failed to load items');
      const data = await res.json();
      setItemsCache((prev) => ({ ...prev, [type]: { items: data.items || [], total: data.total || 0, loading: false } }));
    } catch (e) {
      setItemsCache((prev) => ({ ...prev, [type]: { items: [], total: 0, loading: false, error: e.message } }));
    }
  }, [teamId, getToken]);

  const runAnalyzer = async (type) => {
    try {
      setError('');
      const h = await getAuthHeaders(getToken);
      const res = await fetch(`${API_BASE_URL}/api/analyzers/${teamId}/run/${type}`, {
        method: 'POST', headers: h
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || 'Failed');
      }
      setStatuses((prev) => ({
        ...prev,
        [type]: { ...prev[type], running: true }
      }));
    } catch (e) {
      setError(e.message);
    }
  };

  const clearAnalyzer = async (type) => {
    setClearModal(null);
    try {
      setError('');
      const h = await getAuthHeaders(getToken);
      const res = await fetch(`${API_BASE_URL}/api/analyzers/${teamId}/clear/${type}`, {
        method: 'DELETE', headers: h
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || 'Failed');
      }
      setItemsCache((prev) => ({ ...prev, [type]: undefined }));
      loadStatus();
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteItem = async (type, itemId) => {
    if (confirmDeleteId !== itemId) {
      setConfirmDeleteId(itemId);
      return;
    }
    setConfirmDeleteId(null);
    try {
      setError('');
      const h = await getAuthHeaders(getToken);
      const res = await fetch(`${API_BASE_URL}/api/analyzers/${teamId}/item/${itemId}`, {
        method: 'DELETE', headers: h
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || 'Failed');
      }
      // Remove from cache
      setItemsCache((prev) => {
        const c = prev[type];
        if (!c) return prev;
        return { ...prev, [type]: { ...c, items: c.items.filter((i) => i.id !== itemId), total: c.total - 1 } };
      });
      // Update status count
      setStatuses((prev) => {
        const s = prev[type];
        if (!s) return prev;
        return { ...prev, [type]: { ...s, itemCount: Math.max(0, (s.itemCount || 1) - 1) } };
      });
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleExpand = (type) => {
    const willExpand = !expanded[type];
    setExpanded((prev) => ({ ...prev, [type]: willExpand }));
    if (willExpand && !itemsCache[type]) {
      loadItems(type);
    }
  };

  const clearDef = clearModal ? ANALYZER_DEFS.find((d) => d.type === clearModal) : null;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Box sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
          <Layers size={22} strokeWidth={2} />
        </Box>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Embeddings
        </Typography>
        <IconButton size="small" onClick={loadStatus} sx={{ ml: 'auto', color: 'text.secondary' }}>
          <RefreshCw size={16} />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <AdminPanelSkeleton variant="list" count={enabledTypes.length || 4} />
      ) : enabledTypes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No enabled analyzers. Enable them in team settings (features: emb-*).
        </Typography>
      ) : (
      <Box display="flex" flexDirection="column" gap={1.5}>
        {enabledTypes.map((def) => {
          const st = statuses[def.type] || {};
          const isRunning = st.running;
          const lastRun = st.lastRun;
          const isExpanded = expanded[def.type];
          const cache = itemsCache[def.type];

          return (
            <Paper key={def.type} variant="outlined" sx={{ p: 2, borderColor: 'divider' }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Database size={18} style={{ color: '#a78bfa', flexShrink: 0 }} />
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle2" fontWeight={600}>{def.label}</Typography>
                    <Chip label={def.type} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                    {st.itemCount > 0 && (
                      <Chip label={`${st.itemCount} items`} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">{def.desc}</Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Play size={14} />}
                  onClick={() => runAnalyzer(def.type)}
                  disabled={isRunning}
                  sx={{ flexShrink: 0 }}
                >
                  {isRunning ? 'Analyzing...' : 'Run'}
                </Button>
                <IconButton size="small" onClick={() => toggleExpand(def.type)} sx={{ color: 'text.secondary' }}>
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </IconButton>
              </Box>

              {isRunning && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}

              <Collapse in={isExpanded}>
                <Box sx={{ mt: 1.5, pl: 4 }}>
                  {/* Last run info */}
                  {lastRun && (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last run: {new Date(lastRun.started_at).toLocaleString()}
                        {' — '}
                        <Chip
                          label={lastRun.status}
                          size="small"
                          color={lastRun.status === 'completed' ? 'success' : lastRun.status === 'failed' ? 'error' : 'warning'}
                          sx={{ fontSize: '0.6rem', height: 16 }}
                        />
                      </Typography>
                      {lastRun.status === 'completed' && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Added: {lastRun.items_added}, Skipped: {lastRun.items_skipped}
                        </Typography>
                      )}
                      {lastRun.error_message && (
                        <Typography variant="caption" color="error" display="block">
                          Error: {lastRun.error_message}
                        </Typography>
                      )}
                    </>
                  )}

                  {/* Items preview */}
                  {cache?.loading && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <CircularProgress size={14} />
                      <Typography variant="caption" color="text.secondary">Loading items...</Typography>
                    </Box>
                  )}
                  {cache?.error && (
                    <Typography variant="caption" color="error" display="block" mt={1}>{cache.error}</Typography>
                  )}
                  {cache?.items?.length > 0 && (
                    <Box mt={1.5}>
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                        <Eye size={12} style={{ color: 'rgba(255,255,255,0.5)' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                          {cache.total} items
                        </Typography>
                      </Box>
                      <Box display="flex" flexDirection="column" gap={1.5}>
                        {cache.items.map((item, idx) => (
                          <Paper
                            key={item.id || idx}
                            variant="outlined"
                            sx={{
                              p: 2, borderColor: 'rgba(255,255,255,0.08)',
                              bgcolor: 'rgba(255,255,255,0.02)',
                              '&:hover': { borderColor: 'rgba(255,255,255,0.15)' }
                            }}
                          >
                            <Box display="flex" alignItems="flex-start" gap={1}>
                              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.2)', minWidth: 24, textAlign: 'right', mt: '2px' }}>
                                {idx + 1}
                              </Typography>
                              <Box flex={1} minWidth={0}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#a78bfa', display: 'block', fontSize: '0.85rem', mb: 0.75 }}>
                                  {item.source_meta?.characterName || item.source_meta?.chapterName || item.source_meta?.storageName || item.source_id || '—'}
                                </Typography>
                                {item.structured_data && (
                                  <Box mb={0.5}>
                                    <StructuredCard data={item.structured_data} analyzerType={def.type} />
                                  </Box>
                                )}
                                {item.normalized_text && !item.structured_data && (
                                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.8rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {item.normalized_text}
                                  </Typography>
                                )}
                              </Box>
                              <Tooltip title={confirmDeleteId === item.id ? 'Нажмите ещё раз для удаления' : 'Удалить'}>
                                <IconButton
                                  size="small"
                                  onClick={() => deleteItem(def.type, item.id)}
                                  sx={{
                                    flexShrink: 0, mt: '-2px',
                                    color: confirmDeleteId === item.id ? 'error.main' : 'rgba(255,255,255,0.2)',
                                    '&:hover': { color: 'error.main' }
                                  }}
                                >
                                  <Trash2 size={14} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                      {cache.total > cache.items.length && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                          ... and {cache.total - cache.items.length} more
                        </Typography>
                      )}
                    </Box>
                  )}
                  {cache && !cache.loading && cache.items?.length === 0 && !cache.error && (
                    <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                      No items yet
                    </Typography>
                  )}

                  {/* Clear button at bottom of expanded view */}
                  {st.itemCount > 0 && (
                    <Box mt={2} display="flex" justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Trash2 size={14} />}
                        onClick={() => setClearModal(def.type)}
                        disabled={isRunning}
                        sx={{ flexShrink: 0 }}
                      >
                        Clear Data
                      </Button>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Box>
      )}

      {/* Clear confirmation modal */}
      <Dialog
        open={!!clearModal}
        onClose={() => setClearModal(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Clear Analyzer Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete all embedding data for <strong>{clearDef?.label || clearModal}</strong> ({clearModal}). This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setClearModal(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => clearAnalyzer(clearModal)} startIcon={<Trash2 size={14} />}>
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
